import { useState, useEffect, useCallback } from 'react';
import { useLocation } from '@react-router';
import type { FormikProps } from 'formik';
import { useRouterHelper, useTranslation } from '@shared/core';
import { useContactCollectorGuestTelemetry } from '../../ContactCollectorGuest.telemetry';
import { contactCollectorGuestRoutePaths } from '../../contactCollectorGuest.routes';
import type { Contacts, IframeContacts } from '../../shared/types';
import type { AddressVerificationResponseType, InternationalAddressResponseType } from '@apps/registry/common/utils/smartyClient';
import { useAddressVerification, useAddressAutocomplete } from '@apps/registry/common/utils/smartyClient';
import { isUnitedStatesCode } from '@shared/utils/address';

export interface SuggestedAddress extends Readonly<{ address: string; city: string; state: string; zipCode: string }> {}

export type AddressOption = 'original' | 'suggested';

export const useAddressController = (formik: FormikProps<Contacts> | FormikProps<IframeContacts>, isIframeVersion: boolean) => {
  const location = useLocation();
  const { handleInternationalAddressSearchV2 } = useAddressAutocomplete();
  const { fetchAddressSuggestions } = useAddressVerification();
  const [isValidatingAddress, setIsValidatingAddress] = useState<boolean>(true);
  const [addressOption, setAddressOption] = useState<AddressOption>('original');
  const [suggestedAddress, setSuggestedAddress] = useState<SuggestedAddress | undefined>(undefined);
  const { buttonInteracted, trackError } = useContactCollectorGuestTelemetry();
  const routeHelpers = useRouterHelper();
  const { t } = useTranslation('contactCollector');
  const tAdmin = t('admin');
  const [headOfHousehold, ...restOfContacts] = formik.values.contacts;

  const handleInternationalAddressAutocomplete = useCallback(
    async (value: string, countryCode: string) => {
      const lookup = { search: value, country: countryCode, includeOnlyPostalCode: headOfHousehold.postalCode };

      const response = await handleInternationalAddressSearchV2(lookup);
      return response;
    },
    [headOfHousehold.postalCode, handleInternationalAddressSearchV2],
  );

  const fetchSuggestions = useCallback(async () => {
    try {
      const isNationalAddress = isUnitedStatesCode(headOfHousehold.countryCode);
      const suggestions = await (isNationalAddress
        ? fetchAddressSuggestions({
            street: headOfHousehold.address1 ?? '',
            city: headOfHousehold.city ?? '',
            state: headOfHousehold.region ?? '',
            zipCode: headOfHousehold.postalCode ?? '',
          })
        : handleInternationalAddressAutocomplete(headOfHousehold.address1 ?? '', headOfHousehold.countryCode ?? ''));

      if (suggestions?.length) {
        setAddressOption('suggested');

        if (isNationalAddress) {
          const suggestion = suggestions[0] as AddressVerificationResponseType;
          const suggestionComponents = suggestion.components;
          setSuggestedAddress({
            address: suggestion.deliveryLine1,
            city: suggestionComponents.cityName,
            state: suggestionComponents.state,
            zipCode: suggestionComponents.zipCode,
          });

          const areAddressesEqual =
            suggestion.deliveryLine1 === (headOfHousehold.address1 ?? '').trim() &&
            suggestionComponents.cityName === (headOfHousehold.city ?? '').trim() &&
            suggestionComponents.state === (headOfHousehold.region ?? '').trim() &&
            suggestionComponents.zipCode === (headOfHousehold.postalCode ?? '').trim();

          if (areAddressesEqual) {
            formik.setValues({
              contacts: [
                {
                  ...headOfHousehold,
                  address1: suggestion.deliveryLine1,
                  city: suggestionComponents.cityName,
                  region: suggestionComponents.state,
                  postalCode: suggestionComponents.zipCode,
                  validated: true,
                },
                ...restOfContacts,
              ],
            } as Contacts);
            if (isIframeVersion) {
              return formik.handleSubmit();
            }
            routeHelpers.goToExactRoute(contactCollectorGuestRoutePaths.confirmation.path, location.search);
          }
        } else {
          const suggestion = suggestions[0] as InternationalAddressResponseType;
          setSuggestedAddress({
            address: suggestion.street,
            city: suggestion.locality,
            state: suggestion.administrativeArea,
            zipCode: suggestion.postalCode,
          });

          const areAddressesEqual =
            suggestion.street === (headOfHousehold.address1 ?? '').trim() &&
            suggestion.locality === (headOfHousehold.city ?? '').trim() &&
            suggestion.administrativeArea === (headOfHousehold.region ?? '').trim() &&
            suggestion.postalCode === (headOfHousehold.postalCode ?? '').trim();

          if (areAddressesEqual) {
            formik.setValues({
              contacts: [
                {
                  ...headOfHousehold,
                  address1: suggestion.street,
                  city: suggestion.locality,
                  region: suggestion.administrativeArea,
                  postalCode: suggestion.postalCode,
                  validated: true,
                },
                ...restOfContacts,
              ],
            } as Contacts);
            if (isIframeVersion) {
              return formik.handleSubmit();
            }
            routeHelpers.goToExactRoute(contactCollectorGuestRoutePaths.confirmation.path, location.search);
          }
        }
      } else {
        setAddressOption('original');
        setSuggestedAddress(undefined);
      }
    } catch (error) {
      trackError('Other', error);
      setAddressOption('original');
      setSuggestedAddress(undefined);
    }
    setIsValidatingAddress(false);
  }, [headOfHousehold, fetchAddressSuggestions, handleInternationalAddressAutocomplete, formik, restOfContacts, isIframeVersion, routeHelpers, trackError, location.search]);

  useEffect(() => {
    if (!formik.isValid) {
      return routeHelpers.goToExactRoute(isIframeVersion ? contactCollectorGuestRoutePaths.form.path : contactCollectorGuestRoutePaths.landing.path, location.search);
    }
    fetchSuggestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOnConfirmAddressClick = () => {
    buttonInteracted({ buttonType: 'save', element: 'confirmAddress', extraInfo: { addressOption } });
    if (addressOption === 'suggested' && suggestedAddress) {
      formik.setValues({
        contacts: [
          {
            ...headOfHousehold,
            address1: suggestedAddress.address,
            city: suggestedAddress.city,
            region: suggestedAddress.state,
            postalCode: suggestedAddress.zipCode,
            validated: true,
          },
          ...restOfContacts,
        ],
      } as Contacts);
    } else {
      formik.setValues({
        contacts: [{ ...headOfHousehold, validated: false }, ...restOfContacts],
      } as Contacts);
    }
    if (isIframeVersion) {
      return formik.handleSubmit();
    }
    routeHelpers.goToExactRoute(contactCollectorGuestRoutePaths.confirmation.path, location.search);
  };

  const handleOnBackClick = () => {
    buttonInteracted({ buttonType: 'navigation', element: 'confirmAddressBack' });
    routeHelpers.goToExactRoute(contactCollectorGuestRoutePaths.form.path, location.search);
  };

  return {
    isValidatingAddress,
    addressOption,
    setAddressOption,
    suggestedAddress,
    tAdmin,
    handleOnConfirmAddressClick,
    handleOnBackClick,
  };
};
